import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { t } from 'ember-intl';
import { service } from '@ember/service';

interface MmNavbarSignature {
  Element: HTMLElement;
  Args: {
    iconFlipped?: boolean;
  };
}

export default class MmNavbarComponent extends Component<MmNavbarSignature> {
  @service('intl') declare intl: any;
  @service('theme') declare theme: any;
  @tracked collapsed = true;
  @tracked localeKey: string = 'en';

  constructor(owner: unknown, args: MmNavbarSignature['Args']) {
    super(owner, args);
    this.updateLocale();
  }

  updateLocale() {
    const locales = this.intl.locale;
    this.localeKey = Array.isArray(locales) ? locales[0] || 'en' : locales || 'en';
  }

  get currentLocale(): string {
    return this.localeKey;
  }

  get isEnglish(): boolean {
    return this.localeKey === 'en';
  }

  @action
  toggleCollapsed() {
    this.collapsed = !this.collapsed;
  }

  @action
  closeMenuIfMobile() {
    // Only collapse on mobile (< 768px)
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      this.collapsed = true;
    }
  }

  @action
  toggleLocale() {
    let newLocale: string;
    let cookieValue: string;

    if (this.localeKey === 'en') {
      newLocale = 'es';
      cookieValue = 'es';
    } else {
      newLocale = 'en';
      cookieValue = 'en';
    }

    // Set cookie (expires in 1 year)
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);
    document.cookie = `locale=${cookieValue}; expires=${expires.toUTCString()}; path=/`;

    // Set locale
    this.intl.setLocale([newLocale]);

    // Update tracked locale
    this.localeKey = newLocale;
  }

  <template>
    <nav id="navbar" class="navbar navbar-default navbar-fixed-top white animated-3" role="navigation" aria-label="Main navigation" ...attributes>
      <div class="container-fluid">
        <div class="navbar-header">
          <button
            type="button"
            class="navbar-toggle {{if this.collapsed 'collapsed' ''}}"
            {{on "click" this.toggleCollapsed}}
            aria-label="Toggle navigation"
          >
            <span class="navbar-toggle-icon" aria-hidden="true"></span>
          </button>

          <LinkTo @route="index" class="navbar-brand" aria-label="Marcemira home">
            <img
              src="/img/Marcemira-avatar.svg"
              class="flip {{if @iconFlipped 'flipped' ''}}"
              alt="Marcemira avatar logo"
              width="40"
              height="40"
              fetchpriority="high"
            />
            Marcemira
          </LinkTo>
        </div>

        <div class="navbar-collapse {{unless this.collapsed 'in' ''}}" id="navbar-menu" aria-label="Site navigation">
          <ul class="nav navbar-nav navbar-right" role="menubar">
            <li role="none">
              <LinkTo
                @route="about"
                class="animated-3"
                role="menuitem"
                {{on "click" this.closeMenuIfMobile}}
              >
                {{t "navbar.about"}}
              </LinkTo>
            </li>
            <li role="none">
              <button
                type="button"
                {{on "click" this.toggleLocale}}
                class="animated-3"
                role="menuitem"
                title="{{if this.isEnglish 'Cambiar a Español' 'Switch to English'}}"
                aria-label="{{if this.isEnglish 'Change language to Spanish' 'Change language to English'}}"
              >
                {{if this.isEnglish "ENG" "ES"}}
              </button>
            </li>
            <li role="none">
              <button
                type="button"
                {{on "click" this.theme.toggleTheme}}
                class="animated-3"
                role="menuitem"
                title="{{if this.theme.isDark 'Switch to Light Mode' 'Switch to Dark Mode'}}"
                aria-label="{{if this.theme.isDark 'Switch to light mode' 'Switch to dark mode'}}"
              >
                <span aria-hidden="true">{{if this.theme.isDark "☀️" "🌙"}}</span>
              </button>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  </template>
}
